// PATH IN YOUR PROJECT: src/components/Header/HeaderIconButton/HeaderIconButton.js
import React from "react"
import PropTypes from "prop-types"

/**
 * HeaderIconButton
 * ------------------------------------------------------------------
 * Single-icon circular button used across the header's right zone
 * (Search, Help, and as the clickable toggle for NotificationDropdown).
 * Accepts an optional unread/count badge.
 *
 * @param {string} iconSrc - path to the icon image (Figma export)
 * @param {string} label - accessible name, also used as the tooltip
 * @param {number} badgeCount - if > 0, renders a small badge (caps at "99+")
 * @param {boolean} active - visually pressed state (e.g. dropdown open)
 */
const HeaderIconButton = React.forwardRef(
  ({ iconSrc, label, badgeCount = 0, active = false, onClick, className = "", ...rest }, ref) => {
    const count = Number(badgeCount)
    const hasBadge = Number.isFinite(count) && count > 0
    const badgeLabel = count > 99 ? "99+" : String(Math.floor(count))

    return (
      <button
        ref={ref}
        type="button"
        className={`header-icon-btn${active ? " header-icon-btn--active" : ""} ${className}`}
        aria-label={hasBadge ? `${label}, ${badgeLabel} unread` : label}
        title={label}
        onClick={onClick}
        {...rest}
      >
        <img src={iconSrc} alt="" className="header-icon-btn__icon" />
        {hasBadge && (
          <span className="header-icon-btn__badge" aria-hidden="true">
            {badgeLabel}
          </span>
        )}
      </button>
    )
  }
)

HeaderIconButton.displayName = "HeaderIconButton"

HeaderIconButton.propTypes = {
  iconSrc: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  badgeCount: PropTypes.number,
  active: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
}

export default HeaderIconButton